/**
 * @fileoverview Primary contact email, birth date, gender, and area fields.
 */

import React from "react";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import Select from "react-select";
import { ReactComponent as MailIcon } from "../../../assets/icons/ContactInformation/MailIcon.svg";
import { getSelectedOption, selectStyles } from "../../../utils/common";
import { SHOW_IN_PROFILE_KEY } from "./contactsDataHelpers";
import styles from "./ContactsData.module.css";

const PrimaryEmailField = ({ form }) => {
  const {
    t,
    correoElectronico,
    setCorreoElectronico,
    showEmail,
    setShowEmail,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.emailAddressLabel")}
      </label>
      <div className={styles.inputWrapper}>
        <MailIcon className={styles.inputIcon} />
        <input
          type="email"
          className={`${styles.input} ${styles.inputWithIcon} ${
            getFieldValidationState(
              "contacto1CorreoElectronico",
              correoElectronico,
            ) === "error"
              ? styles.inputError
              : ""
          }`}
          placeholder={t("components.contactsData.emailAddressPlaceholder")}
          onChange={(e) => {
            setCorreoElectronico(e.target.value);
            handleValidatedFieldChange(
              "contacto1CorreoElectronico",
              e.target.value,
            );
          }}
          value={correoElectronico}
        />
      </div>
      {getErrorMessage("contacto1CorreoElectronico") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("contacto1CorreoElectronico")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setShowEmail(!showEmail);
            handleInputChange("contacto1MostrarCorreoElectronico", !showEmail);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showEmail ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showEmail ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const PrimaryBirthDateField = ({ form }) => {
  const {
    t,
    fechaNacimiento,
    setFechaNacimiento,
    mostrarFechaNacimiento,
    setMostrarFechaNacimiento,
    getFieldValidationState,
    getErrorMessage,
    handleInputChange,
    handleValidatedFieldChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.dateOfBirthLabel")}
      </label>
      <div className={styles.inputWrapper}>
        <input
          type="date"
          name="contacto1FechaNacimiento"
          placeholder={t("common.dateFormatPlaceholder")}
          className={`${styles.input} ${
            getFieldValidationState(
              "contacto1FechaNacimiento",
              fechaNacimiento,
            ) === "error"
              ? styles.inputError
              : ""
          }`}
          value={fechaNacimiento}
          onChange={(e) => {
            const value = e.target.value;
            setFechaNacimiento(value);
            handleValidatedFieldChange("contacto1FechaNacimiento", value);
          }}
        />
      </div>
      {getErrorMessage("contacto1FechaNacimiento") && (
        <p className={styles.errorMessage}>
          {getErrorMessage("contacto1FechaNacimiento")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setMostrarFechaNacimiento(!mostrarFechaNacimiento);
            handleInputChange(
              "contacto1MostrarFechaNacimiento",
              !mostrarFechaNacimiento,
            );
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: mostrarFechaNacimiento ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {mostrarFechaNacimiento ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const PrimaryGenderField = ({ form, onFieldChange }) => {
  const {
    t,
    genderOptions,
    genero,
    setGenero,
    mostrarGenero,
    setMostrarGenero,
    handleInputChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.genderLabel")}
      </label>
      <Select
        value={getSelectedOption(genderOptions, genero)}
        onChange={(selectedOption) => {
          setGenero(selectedOption.value);
          onFieldChange("contacto1Genero", selectedOption.value);
        }}
        options={genderOptions}
        placeholder={t("components.contactsData.genderPlaceholder")}
        styles={selectStyles}
        className="select__control"
      />
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setMostrarGenero(!mostrarGenero);
            handleInputChange("contacto1MostrarGenero", !mostrarGenero);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: mostrarGenero ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {mostrarGenero ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const PrimaryAreaField = ({ form }) => {
  const {
    t,
    areaOptions,
    area,
    setArea,
    mostrarArea,
    setMostrarArea,
    handleInputChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.areaLabel")}
      </label>
      <Select
        value={getSelectedOption(areaOptions, area)}
        options={areaOptions}
        placeholder={t("components.contactsData.areaPlaceholder")}
        styles={selectStyles}
        className="select__control"
        onChange={(selectedOption) => {
          setArea(selectedOption.value);
          handleInputChange("contacto1Area", selectedOption.value);
        }}
      />
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(SHOW_IN_PROFILE_KEY)}
        </span>
        <IconButton
          onClick={() => {
            setMostrarArea(!mostrarArea);
            handleInputChange("contacto1MostrarArea", !mostrarArea);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: mostrarArea ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {mostrarArea ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

export const PrimaryContactProfileFields = ({ form, onFieldChange }) => (
  <div className={styles.inputRow}>
    <PrimaryEmailField form={form} />
    <PrimaryBirthDateField form={form} />
    <PrimaryGenderField form={form} onFieldChange={onFieldChange} />
    <PrimaryAreaField form={form} />
  </div>
);
